import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  Package,
  Factory,
  CheckCircle2,
  Clock,
  Play,
  Pause,
  Send,
  AlertCircle,
  Warehouse,
  X,
  Edit2,
  FileText,
  Download,
  Layers,
  Check,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  User,
  UserCheck,
} from "lucide-react";
import { api, downloadDocument } from "../../lib/api";
import { money, getTodayDateString, compactMoney } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import MoveBackStageModal from "./MoveBackStageModal";
import OrderHistoryTrail from "./OrderHistoryTrail";

export default function ProductionDetail({ orderId, onBack, onChange, user }) {
  const [order, setOrder] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [selectedProductOrderId, setSelectedProductOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthorizedMsg, setUnauthorizedMsg] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [startDate, setStartDate] = useState("");

  // History collapse toggle
  const [historyOpen, setHistoryOpen] = useState(false);

  // Lists
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Modal states
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showUpdateHoldModal, setShowUpdateHoldModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [movingBack, setMovingBack] = useState(false);

  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showBatchAssignUserModal, setShowBatchAssignUserModal] = useState(false);
  const [productionUsers, setProductionUsers] = useState([]);
  // Batch Modals
  const [showBatchAssignModal, setShowBatchAssignModal] = useState(false);
  const [showBatchStartModal, setShowBatchStartModal] = useState(false);
  const [showBatchDateModal, setShowBatchDateModal] = useState(false);

  const canAct = isAdmin(user) || canAccessDepartment(user, ["Production"]);
  const isBatchMode = Boolean(batchData && batchData.orders && batchData.orders.length > 1);

  // Load order / batch details
  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const isDirectBatch = typeof orderId === "string" && orderId.toUpperCase().startsWith("BATCH-");

      if (isDirectBatch) {
        const batchRes = await api("get", `/production/batch/${orderId}`);
        const bData = batchRes.data;
        setBatchData(bData);
        if (bData.orders && bData.orders.length > 0) {
          const activeOrder = (selectedProductOrderId && bData.orders.find((o) => o.order_id === selectedProductOrderId)) || bData.orders[0];
          setOrder(activeOrder);
          setSelectedProductOrderId(activeOrder.order_id);
          setStartDate(activeOrder.production_start_date || new Date().toISOString().slice(0, 10));
        }
        return;
      }

      // Single order request
      const res = await api("get", `/orders/${orderId}`);
      const data = res.data;

      // If this order is part of a bulk batch, load the batch context too
      if (data.upload_batch_id) {
        try {
          const batchRes = await api("get", `/production/batch/${data.upload_batch_id}`);
          if (batchRes.data && batchRes.data.orders && batchRes.data.orders.length > 1) {
            setBatchData(batchRes.data);
            setSelectedProductOrderId(data.order_id);
          } else {
            setBatchData(null);
          }
        } catch (e) {
          setBatchData(null);
        }
      } else {
        setBatchData(null);
      }

      setOrder(data);
      setStartDate(data.production_start_date || new Date().toISOString().slice(0, 10));
    } catch (err) {
      console.error("Failed to load production order:", err);
      const detail = err.response?.data?.detail || "Could not load production details";
      toast.error(detail);
      if (err.response?.status === 403) {
        setUnauthorizedMsg(detail);
      }
    } finally {
      setLoading(false);
    }
  }, [orderId, selectedProductOrderId]);

  const loadActiveResources = useCallback(() => {
    api("get", "/vendors?status=Active").then((r) => setVendors(r.data || [])).catch(() => {});
    api("get", "/warehouses?status=Active").then((r) => setWarehouses(r.data || [])).catch(() => {});
    api("get", "/users?active_only=true").then((r) => {
      const allActive = r.data || [];
      const prodEligible = allActive.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          (u.role === "admin" ||
            u.role === "super_admin" ||
            (u.department || "").toLowerCase() === "production" ||
            (u.department || "").toLowerCase() === "management" ||
            (u.departments || []).some((d) => ["production", "management"].includes(d.toLowerCase())))
      );
      setProductionUsers(prodEligible);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadOrder();
    loadActiveResources();
  }, [loadOrder, loadActiveResources]);

  // Switch active product inside batch
  const handleSelectProduct = (productOrder) => {
    setSelectedProductOrderId(productOrder.order_id);
    setOrder(productOrder);
    setStartDate(productOrder.production_start_date || new Date().toISOString().slice(0, 10));
  };

  // Start Production (Individual)
  const handleStartProduction = async (targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`start_${targetOrderId}`);
    try {
      await api("patch", `/orders/${targetOrderId}/production`, {
        status: "In Progress",
        start_date: startDate,
        remarks: "Production started",
      });
      toast.success("Production started");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start production";
      toast.error(typeof msg === "string" ? msg : "Start failed");
    } finally {
      setBusyAction("");
    }
  };

  // Update Production (Individual)
  const handleSaveProductionUpdate = async ({ estDate, remark }, targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`update_${targetOrderId}`);
    try {
      await api("patch", `/orders/${targetOrderId}/production`, {
        expected_completion: estDate || "",
        estimated_ready_date: estDate || "",
        remarks: remark || "",
      });
      toast.success("Production updates saved");
      setShowUpdateModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update production";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyAction("");
    }
  };

  // Put on Hold (Individual)
  const handlePutOnHold = async ({ holdReason, nextEstDate }, targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    if (!holdReason || !holdReason.trim()) {
      toast.error("Hold reason is required");
      return;
    }
    if (!nextEstDate) {
      toast.error("Next estimated production ready date is required");
      return;
    }
    setBusyAction(`hold_${targetOrderId}`);
    try {
      await api("patch", `/orders/${targetOrderId}/production`, {
        status: "On Hold",
        hold_reason: holdReason.trim(),
        expected_completion: nextEstDate,
        estimated_ready_date: nextEstDate,
        remarks: holdReason.trim(),
      });
      toast.success("Production put On Hold");
      setShowHoldModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not put order on hold";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyAction("");
    }
  };

  // Update Hold Details (Individual)
  const handleSaveHoldDetails = async ({ holdReason, nextEstDate }, targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    if (!holdReason || !holdReason.trim()) {
      toast.error("Hold reason is required");
      return;
    }
    if (!nextEstDate) {
      toast.error("Next estimated production ready date is required");
      return;
    }
    setBusyAction(`hold_update_${targetOrderId}`);
    try {
      await api("patch", `/orders/${targetOrderId}/production`, {
        hold_reason: holdReason.trim(),
        expected_completion: nextEstDate,
        estimated_ready_date: nextEstDate,
        remarks: holdReason.trim(),
      });
      toast.success("Hold details updated");
      setShowUpdateHoldModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update hold details";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyAction("");
    }
  };

  // Resume Production (Individual)
  const handleResumeProduction = async ({ estDate, remark }, targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`resume_${targetOrderId}`);
    try {
      await api("patch", `/orders/${targetOrderId}/production`, {
        status: "In Progress",
        expected_completion: estDate || "",
        estimated_ready_date: estDate || "",
        remarks: remark || "Production resumed",
      });
      toast.success("Production resumed");
      setShowResumeModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not resume production";
      toast.error(typeof msg === "string" ? msg : "Resume failed");
    } finally {
      setBusyAction("");
    }
  };

  // Mark as Completed (Individual)
  const handleMarkCompleted = async (targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`complete_${targetOrderId}`);
    try {
      await api("patch", `/orders/${targetOrderId}/production`, {
        status: "Completed",
        actual_completion: new Date().toISOString().slice(0, 10),
        remarks: "Production completed",
        handover_to_dispatch: false,
      });
      toast.success("Production completed. Order is ready for dispatch.");
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not complete production";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyAction("");
    }
  };

  // Send to Dispatch (Individual)
  const handleReadyForDispatch = async (targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`dispatch_${targetOrderId}`);
    try {
      await api("post", `/orders/${targetOrderId}/send-to-dispatch`);
      toast.success("Order sent to Dispatch successfully!");
      await loadOrder();
      onChange && onChange();
      if (!isBatchMode) {
        setTimeout(() => onBack(), 800);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not send order to dispatch";
      toast.error(typeof msg === "string" ? msg : "Dispatch handover failed");
    } finally {
      setBusyAction("");
    }
  };

  // Assign Vendor / Warehouse (Individual)
  const handleAssignDetails = async ({ vendorId, warehouseId }, targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    const v = vendors.find((x) => x.vendor_id === vendorId);
    const w = warehouses.find((x) => x.warehouse_id === warehouseId);

    setBusyAction(`assign_${targetOrderId}`);
    try {
      await api("post", `/orders/${targetOrderId}/confirm-production-details`, {
        vendor_id: vendorId || "",
        vendor_name: v?.name || v?.vendor_name || "",
        warehouse_id: warehouseId || "",
        warehouse_name: w?.name || w?.warehouse_name || "",
      });
      toast.success("Production Vendor & Warehouse assigned");
      setShowAssignModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not assign details";
      toast.error(typeof msg === "string" ? msg : "Assignment failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Assign Vendor & Warehouse
  const handleBatchAssign = async ({ vendorId, warehouseId, orderIds }) => {
    if (!batchData) return;
    const v = vendors.find((x) => x.vendor_id === vendorId);
    const w = warehouses.find((x) => x.warehouse_id === warehouseId);

    setBusyAction("batch_assign");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/production`, {
        order_ids: orderIds && orderIds.length > 0 ? orderIds : null,
        vendor_id: vendorId || "",
        vendor_name: v?.name || v?.vendor_name || "",
        warehouse_id: warehouseId || "",
        warehouse_name: w?.name || w?.warehouse_name || "",
      });
      toast.success(`Assigned Vendor/Warehouse to ${orderIds?.length || batchData.orders.length} products`);
      setShowBatchAssignModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update batch assignment";
      toast.error(typeof msg === "string" ? msg : "Batch update failed");
    } finally {
      setBusyAction("");
    }
  };
  // Assign Production User (Individual)
  const handleAssignProductionUser = async ({ assignedUser, assignedUserId, remarks }) => {
    if (!order) return;
    setBusyAction("assign_user");
    try {
      await api("patch", `/orders/${order.order_id}/assign-production-user`, {
        assigned_user: assignedUser,
        assigned_user_id: assignedUserId,
        remarks: remarks || "Assigned production user",
      });
      toast.success(`Assigned ${assignedUser || "Unassigned"} to #${order.order_id}`);
      setShowAssignUserModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not assign production user";
      toast.error(typeof msg === "string" ? msg : "Failed to assign user");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Assign Production User
  const handleBatchAssignProductionUser = async ({ assignedUser, assignedUserId, orderIds, remarks }) => {
    if (!batchData) return;
    setBusyAction("batch_assign_user");
    try {
      const targetIds = orderIds && orderIds.length > 0 ? orderIds : batchData.orders.map((o) => o.order_id);
      await api("patch", `/orders/batch/${batchData.batch_id}/assign-production-user`, {
        order_ids: targetIds,
        assigned_user: assignedUser,
        assigned_user_id: assignedUserId,
        remarks: remarks || "Batch assigned production user",
      });
      toast.success(`Assigned ${assignedUser || "Unassigned"} to ${targetIds.length} products`);
      setShowBatchAssignUserModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not assign production user across batch";
      toast.error(typeof msg === "string" ? msg : "Failed to assign user");
    } finally {
      setBusyAction("");
    }
  };


  // Batch Start Production
  const handleBatchStart = async ({ startDateVal, orderIds }) => {
    if (!batchData) return;
    setBusyAction("batch_start");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/production`, {
        order_ids: orderIds && orderIds.length > 0 ? orderIds : null,
        status: "In Progress",
        start_date: startDateVal || new Date().toISOString().slice(0, 10),
        remarks: "Production started (Bulk Batch)",
      });
      toast.success(`Started production for ${orderIds?.length || batchData.orders.length} products`);
      setShowBatchStartModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start batch production";
      toast.error(typeof msg === "string" ? msg : "Batch start failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Update Ready Dates
  const handleBatchUpdateDates = async ({ estDate, remark, orderIds }) => {
    if (!batchData) return;
    setBusyAction("batch_dates");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/production`, {
        order_ids: orderIds && orderIds.length > 0 ? orderIds : null,
        expected_completion: estDate || "",
        estimated_ready_date: estDate || "",
        remarks: remark || "",
      });
      toast.success(`Updated dates for ${orderIds?.length || batchData.orders.length} products`);
      setShowBatchDateModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not update batch dates";
      toast.error(typeof msg === "string" ? msg : "Batch update failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Mark Completed (remains in Production queue until sent to dispatch)
  const handleBatchMarkCompleted = async () => {
    if (!batchData) return;
    setBusyAction("batch_complete");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/production`, {
        status: "Completed",
        actual_completion: new Date().toISOString().slice(0, 10),
        remarks: "Bulk production completed",
        handover_to_dispatch: false,
      });
      toast.success(`Production marked Completed for products in #${batchData.batch_id}`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not complete batch production";
      toast.error(typeof msg === "string" ? msg : "Batch action failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Send Completed to Dispatch
  const handleBatchSendToDispatch = async () => {
    if (!batchData) return;
    const completedOrders = (batchData.orders || []).filter(
      (o) => o.production_status === "Completed"
    );
    if (completedOrders.length === 0) {
      toast.error("No completed products found to send to Dispatch.");
      return;
    }
    setBusyAction("batch_dispatch");
    try {
      await api("post", `/orders/batch/${batchData.batch_id}/send-to-dispatch`, {
        order_ids: completedOrders.map((o) => o.order_id),
      });
      toast.success(`Sent ${completedOrders.length} completed product(s) to Dispatch!`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not send batch to dispatch";
      toast.error(typeof msg === "string" ? msg : "Handover failed");
    } finally {
      setBusyAction("");
    }
  };

  if (loading && !order && !batchData) {
    return (
      <div className="content">
        <div className="loading-screen">Loading production details…</div>
      </div>
    );
  }

  if (!order && !batchData) {
    return (
      <div className="content">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Production Queue
        </button>
        <div className="panel" style={{ padding: 32, textAlign: "center", marginTop: 16 }}>
          <h3 style={{ margin: 0, color: "var(--navy)" }}>{unauthorizedMsg ? "Access Restricted" : "Production order not found"}</h3>
          <p className="muted" style={{ marginTop: 4 }}>{unauthorizedMsg || "This order might have been moved or deleted."}</p>
        </div>
      </div>
    );
  }

  const currentStatus = order?.production_status || "Not Started";
  const isOverdue = order?.required_delivery_date && new Date(order.required_delivery_date) < new Date();
  const currentEstDate = order?.estimated_ready_date || order?.production_expected || "";
  const currentRemarks = order?.production_remarks || "";
  const canSeePhone = canViewClientPhone(user);

  const statusBadgeStyle = (st) => {
    switch (st) {
      case "Completed":
        return { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" };
      case "In Progress":
        return { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" };
      case "On Hold":
        return { color: "#d97706", bg: "#fffbeb", border: "#fde68a" };
      default:
        return { color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd" };
    }
  };

  return (
    <div className="content" style={{ maxWidth: 1280, margin: "0 auto", paddingBottom: 40 }}>
      {/* Top Bar: Back Button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button className="back-btn" onClick={onBack} data-testid="production-back-button" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
          <ArrowLeft size={14} /> Back to Production Queue
        </button>
      </div>

      {/* ============================================================== */}
      {/* BATCH HEADER (When viewing a bulk Excel upload batch)           */}
      {/* ============================================================== */}
      {isBatchMode && (
        <div
          className="panel"
          style={{
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 14,
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
          data-testid={`bulk-production-batch-header-${batchData.batch_id}`}
        >
          {/* Top Row: Batch ID, Title & Quick Actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  background: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  padding: "2px 7px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "var(--mono)",
                }}
              >
                #{batchData.batch_id}
              </span>
              <strong style={{ fontSize: 14, color: "var(--navy)" }}>
                {batchData.client?.company_name}
              </strong>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                · {batchData.orders.length} Products · Total: <b style={{ color: "var(--navy)" }}>{compactMoney(batchData.total_value)}</b>
              </span>
              {batchData.client?.contact_person && batchData.client.contact_person !== "—" && (
                <span style={{ fontSize: 11.5, color: "#64748b" }}>
                  (Contact: {batchData.client.contact_person})
                </span>
              )}
            </div>

            {/* Batch Level Action Buttons */}
            {canAct && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => setShowBatchAssignModal(true)}
                  disabled={Boolean(busyAction)}
                  data-testid="batch-assign-vendor-btn"
                  title="Assign vendor and warehouse across all products in this batch"
                  style={{ height: 28, fontSize: 11.5, padding: "0 9px" }}
                >
                  <Factory size={12} /> Assign All
                </button>
                {isAdmin(user) && (
                  <button
                    type="button"
                    className="outline-btn compact"
                    onClick={() => setShowBatchAssignUserModal(true)}
                    disabled={Boolean(busyAction)}
                    data-testid="batch-assign-user-btn"
                    title="Assign internal production user across all products in this batch"
                    style={{ height: 28, fontSize: 11.5, padding: "0 9px", display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <UserCheck size={12} /> Assign User
                  </button>
                )}
                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => setShowBatchDateModal(true)}
                  disabled={Boolean(busyAction)}
                  data-testid="batch-update-dates-btn"
                  title="Update ready dates across this batch"
                  style={{ height: 28, fontSize: 11.5, padding: "0 9px" }}
                >
                  <Calendar size={12} /> Set Ready Dates
                </button>
                {(batchData.counts?.["Not Started"] || 0) > 0 && (
                  <button
                    type="button"
                    className="primary-btn compact"
                    style={{ background: "#4f46e5", borderColor: "#4f46e5", height: 28, fontSize: 11.5, padding: "0 10px" }}
                    onClick={() => setShowBatchStartModal(true)}
                    disabled={Boolean(busyAction)}
                    data-testid="batch-start-production-btn"
                    title="Start production for products in this batch"
                  >
                    <Play size={12} /> Batch Start ({batchData.counts?.["Not Started"]})
                  </button>
                )}
                {batchData.all_handed_to_dispatch || (batchData.orders?.length === 0) ? (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      border: "1px solid #bfdbfe",
                      padding: "4px 10px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    <CheckCircle2 size={12} /> All Products Handed Over to Dispatch
                  </div>
                ) : (
                  <>
                    {(batchData.counts?.["Completed"] || 0) < (batchData.counts?.total || 0) && (
                      <button
                        type="button"
                        className="primary-btn compact"
                        style={{ background: "#059669", borderColor: "#059669", height: 28, fontSize: 11.5, padding: "0 10px" }}
                        onClick={handleBatchMarkCompleted}
                        disabled={Boolean(busyAction)}
                        data-testid="batch-complete-production-btn"
                        title="Mark production completed for all remaining products"
                      >
                        <CheckCircle2 size={12} /> Mark All Completed
                      </button>
                    )}
                    {(batchData.counts?.["Completed"] || 0) > 0 && (
                      <button
                        type="button"
                        className="primary-btn compact"
                        style={{ background: "#0284c7", borderColor: "#0284c7", height: 28, fontSize: 11.5, padding: "0 10px" }}
                        onClick={handleBatchSendToDispatch}
                        disabled={Boolean(busyAction)}
                        data-testid="batch-send-dispatch-btn"
                        title="Hand over completed products to Dispatch queue"
                      >
                        <Send size={12} /> Send to Dispatch ({batchData.counts?.["Completed"]})
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Batch Assignment Summary Strip */}
          {(() => {
            const batchUsers = Array.from(new Set(
              (batchData.orders || []).flatMap((o) => {
                const u = o.assigned_producer || o.assigned_production_user || "";
                return u.split(",").map((s) => s.trim()).filter(Boolean);
              })
            ));
            const userDisplay = batchUsers.length > 0
              ? batchUsers.join(", ")
              : (batchData.assigned_producer || batchData.assigned_production_user || "Unassigned");
            const whStaff = Array.from(new Set(
              (batchData.orders || []).flatMap((o) => o.warehouse_assigned_user_names || [])
            )).filter(Boolean).join(", ");

            return (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 10,
                  padding: "7px 10px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                  fontSize: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Factory size={13} color="#4f46e5" />
                  <span style={{ color: "#64748b", fontWeight: 600 }}>Production Vendor:</span>
                  <span style={{ fontWeight: 700, color: batchData.production_vendor_name ? "var(--navy)" : "#d97706" }}>
                    {batchData.production_vendor_name || "Unassigned"}
                  </span>
                </div>
                <span style={{ color: "#cbd5e1" }}>•</span>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <User size={13} color="#059669" />
                  <span style={{ color: "#64748b", fontWeight: 600 }}>Assigned User:</span>
                  <span style={{ fontWeight: 700, color: userDisplay !== "Unassigned" ? "var(--navy)" : "#d97706" }}>
                    {userDisplay}
                  </span>
                </div>
                <span style={{ color: "#cbd5e1" }}>•</span>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Warehouse size={13} color="#0284c7" />
                  <span style={{ color: "#64748b", fontWeight: 600 }}>Warehouse:</span>
                  <span style={{ fontWeight: 700, color: batchData.warehouse_name ? "var(--navy)" : "#d97706" }}>
                    {batchData.warehouse_name || "Unassigned"}
                  </span>
                  {whStaff && (
                    <span style={{ fontSize: 11, color: "#0284c7", fontWeight: 600, background: "#f0f9ff", border: "1px solid #bae6fd", padding: "1px 6px", borderRadius: 4 }}>
                      Staff: {whStaff}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Status Breakdown Bar */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 10,
              paddingTop: 8,
              borderTop: "1px solid #f1f5f9",
              flexWrap: "wrap",
              alignItems: "center",
              fontSize: 11.5,
            }}
          >
            <span style={{ fontWeight: 600, color: "#475569" }}>Batch Status:</span>
            {batchData.counts["Not Started"] > 0 && (
              <span className="badge blue" style={{ fontSize: 10, padding: "1px 6px" }}>
                {batchData.counts["Not Started"]} Not Started
              </span>
            )}
            {batchData.counts["In Progress"] > 0 && (
              <span className="badge purple" style={{ fontSize: 10, padding: "1px 6px" }}>
                {batchData.counts["In Progress"]} In Progress
              </span>
            )}
            {batchData.counts["On Hold"] > 0 && (
              <span className="badge amber" style={{ fontSize: 10, padding: "1px 6px" }}>
                {batchData.counts["On Hold"]} On Hold
              </span>
            )}
            {batchData.counts["Completed"] > 0 && (
              <span className="badge green" style={{ fontSize: 10, padding: "1px 6px" }}>
                {batchData.counts["Completed"]} Completed
              </span>
            )}
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* PRODUCT SELECTOR TABS (Inside Bulk Batch)                     */}
      {/* ============================================================== */}
      {isBatchMode && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#64748b", marginBottom: 6, letterSpacing: "0.5px" }}>
            Products in Bulk Request ({batchData.orders.length}) — Click to switch:
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 8,
            }}
          >
            {batchData.orders.map((o) => {
              const isSelected = order?.order_id === o.order_id;
              const prodSt = o.production_status || "Not Started";
              const bTheme = statusBadgeStyle(prodSt);

              return (
                <div
                  key={o.order_id}
                  onClick={() => handleSelectProduct(o)}
                  style={{
                    cursor: "pointer",
                    background: isSelected ? "#f0f9ff" : "#ffffff",
                    border: isSelected ? "1.5px solid #0284c7" : "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: "7px 10px",
                    boxShadow: isSelected ? "0 1px 3px rgba(2,132,199,0.12)" : "none",
                    transition: "all 0.15s ease",
                  }}
                  data-testid={`batch-product-tab-${o.order_id}`}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <span className="mono" style={{ fontSize: 10.5, fontWeight: 700, color: isSelected ? "#0369a1" : "#64748b" }}>
                      #{o.order_id}
                    </span>
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "1px 5px",
                        borderRadius: 3,
                        color: bTheme.color,
                        background: bTheme.bg,
                        border: `1px solid ${bTheme.border}`,
                      }}
                    >
                      {prodSt}
                    </span>
                  </div>

                  <div style={{ fontWeight: 600, fontSize: 12.5, color: isSelected ? "#0369a1" : "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {o.product}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color: o.description?.trim() ? "#475569" : "#94a3b8",
                      fontStyle: o.description?.trim() ? "normal" : "italic",
                      marginTop: 1,
                      marginBottom: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={o.description?.trim() || "No description provided"}
                  >
                    {o.description?.trim() ? o.description.trim() : "No description provided"}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "#64748b", marginTop: 2 }}>
                    <span>{o.quantity} {o.unit || "pcs"}</span>
                    <span style={{ fontSize: 10.5, color: o.production_vendor_name || o.assigned_producer ? "#334155" : o.warehouse_name ? "#059669" : "#94a3b8" }}>
                      {o.production_vendor_name || o.assigned_producer || o.warehouse_name || "Unassigned"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* INDIVIDUAL ORDER DETAIL HEAD (When viewing single order)       */}
      {/* ============================================================== */}
      {!isBatchMode && (
        <div
          className="panel"
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "14px 18px",
            marginBottom: 14,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>
                #{order.order_id}
              </span>
              <span style={{ color: "#cbd5e1" }}>•</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                {order.client_name}
              </span>
            </div>
            <h1 style={{ margin: "2px 0 4px", fontSize: 20, color: "var(--navy)", fontWeight: 700 }} data-testid="production-product-title">
              {order.product}
            </h1>
            {/* Order Description Directly Below Product Name - Bold, Prominent, Clean, Non-Boxed (Matching Design section) */}
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: order.description?.trim() ? "#0f172a" : "#94a3b8",
                fontStyle: order.description?.trim() ? "normal" : "italic",
                lineHeight: 1.5,
                margin: "4px 0 10px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
              data-testid="production-order-description-text"
            >
              {order.description?.trim() ? order.description.trim() : "No description provided"}
            </div>
            <div style={{ fontSize: 12.5, color: "#64748b", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <span>Quantity: <strong style={{ color: "var(--navy)" }}>{(order.quantity || 0).toLocaleString()} {order.unit || "units"}</strong></span>
              <span>•</span>
              <span>Due: <strong style={{ color: isOverdue ? "#dc2626" : "var(--navy)" }}>{order.required_delivery_date || "—"}</strong></span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {order.current_stage !== "Order Received" && (
              <button
                type="button"
                className="outline-btn compact"
                data-testid="production-header-move-back-btn"
                style={{
                  color: "#b45309",
                  borderColor: "#fde68a",
                  background: "#fffbeb",
                  fontWeight: 600,
                  fontSize: 12,
                  height: 30,
                }}
                onClick={() => setMovingBack(true)}
                title="Move order back to previous stage"
              >
                <RotateCcw size={12} /> Move Back
              </button>
            )}
            <span
              className={`badge ${
                currentStatus === "Completed"
                  ? "green"
                  : currentStatus === "In Progress"
                  ? "purple"
                  : currentStatus === "On Hold"
                  ? "amber"
                  : "blue"
              }`}
              style={{ fontSize: 12, padding: "4px 10px", fontWeight: 600 }}
            >
              {currentStatus}
            </span>
            {order.priority && (
              <span
                className={`badge ${
                  order.priority === "Urgent" ? "red" : order.priority === "High" ? "amber" : "blue"
                }`}
                style={{ fontSize: 12, padding: "4px 10px" }}
              >
                {order.priority}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Overdue Alert */}
      {isOverdue && currentStatus !== "Completed" && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 6,
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
            fontSize: 12.5,
            color: "#991b1b",
          }}
        >
          <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
          <div>
            <b>Production Overdue:</b> Required delivery date was {order.required_delivery_date}.
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* 2-COLUMN WORKSPACE FOR ACTIVE PRODUCT                         */}
      {/* ============================================================== */}
      <div style={{ display: "grid", gridTemplateColumns: "1.9fr 1.1fr", gap: 16, alignItems: "flex-start", marginBottom: 20 }}>
        
        {/* LEFT COLUMN: Order Info, Assignment, and History */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          
          {/* Active Product Banner (if in batch) */}
          {isBatchMode && (
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: "#64748b", display: "block" }}>
                  Active Order in Request
                </span>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
                  {order.product} <span className="mono" style={{ fontSize: 12, color: "#64748b" }}>(#{order.order_id})</span>
                </div>
                {/* Active Order Description */}
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: order.description?.trim() ? "#334155" : "#94a3b8",
                    fontStyle: order.description?.trim() ? "normal" : "italic",
                    marginTop: 3,
                    lineHeight: 1.4,
                  }}
                  data-testid="production-active-order-description"
                >
                  {order.description?.trim() ? order.description.trim() : "No description provided"}
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 4,
                  color: statusBadgeStyle(currentStatus).color,
                  background: statusBadgeStyle(currentStatus).bg,
                  border: `1px solid ${statusBadgeStyle(currentStatus).border}`,
                }}
              >
                {currentStatus}
              </span>
            </div>
          )}

          {/* SECTION 1: ORDER / REQUEST INFORMATION */}
          <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }} data-testid="order-request-info-panel">
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <Package size={16} color="#4f46e5" />
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>Order &amp; Request Info</h3>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "8px 12px", fontSize: 12 }}>
              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Client</span>
                <b style={{ color: "var(--navy)" }}>{order.client_name}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Order ID</span>
                <b className="mono" style={{ color: "#2563eb" }}>#{order.order_id}</b>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Quantity &amp; Value</span>
                <span style={{ color: "#334155" }}>{(order.quantity || 0).toLocaleString()} {order.unit || "units"} {order.total_value > 0 ? `· ${money(order.total_value)}` : ""}</span>
              </div>

              <div>
                <span style={{ color: "#64748b", display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Deadline</span>
                <b style={{ color: isOverdue ? "#dc2626" : "var(--navy)" }}>{order.required_delivery_date || "—"}</b>
              </div>
            </div>

            {/* Order Description */}
            <div style={{ marginTop: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px" }}>
              <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b", display: "block", marginBottom: 2 }}>
                Order Description
              </span>
              <p
                style={{
                  margin: 0,
                  fontSize: 12.5,
                  color: order.description?.trim() ? "var(--navy)" : "#94a3b8",
                  fontStyle: order.description?.trim() ? "normal" : "italic",
                  lineHeight: 1.45,
                  whiteSpace: "pre-wrap",
                }}
              >
                {order.description?.trim() ? order.description.trim() : "No description provided"}
              </p>
            </div>

            {/* Design Specs */}
            <div style={{ marginTop: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px" }}>
              <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b", display: "block", marginBottom: 2 }}>
                Design Specifications &amp; Instructions
              </span>
              <p style={{ margin: 0, fontSize: 12, color: "var(--navy)", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                {order.design_specs || order.notes || "Standard specifications as per order received."}
              </p>
            </div>

            {/* Approved Documents */}
            {(order.documents || []).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b", display: "block", marginBottom: 4 }}>
                  Approved Proof Documents ({(order.documents || []).length})
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {(order.documents || []).map((doc, idx) => (
                    <div
                      key={doc.file_id || idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 4,
                        padding: "5px 8px",
                        fontSize: 11.5,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <FileText size={13} color="#64748b" />
                        <b>{doc.name || `Document ${idx + 1}`}</b>
                        {doc.category && <span style={{ color: "#94a3b8", fontSize: 10.5 }}>({doc.category})</span>}
                      </div>
                      {doc.file_id && (
                        <button
                          type="button"
                          className="outline-btn compact"
                          onClick={() => downloadDocument(doc.file_id, doc.name)}
                          style={{ padding: "1px 6px", fontSize: 10.5, height: 22 }}
                        >
                          <Download size={10} /> Get
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* SECTION 2: PRODUCTION ASSIGNMENT */}
          <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }} data-testid="production-assignment-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Factory size={16} color="#4f46e5" />
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>Production Assignment</h3>
              </div>

              {canAct && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    className="outline-btn compact"
                    onClick={() => setShowAssignModal(true)}
                    data-testid="assign-vendor-warehouse-btn"
                    style={{ height: 26, fontSize: 11, padding: "0 8px" }}
                  >
                    <Edit2 size={11} /> Assign Vendor / Warehouse
                  </button>
                  {isAdmin(user) && (
                    <button
                      type="button"
                      className="outline-btn compact"
                      onClick={() => setShowAssignUserModal(true)}
                      data-testid="assign-user-btn"
                      style={{ height: 26, fontSize: 11, padding: "0 8px", display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <UserCheck size={11} /> Assign User
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {/* 1. Production Vendor Box */}
              <div
                style={{
                  background: order.production_vendor_name ? "#f0fdf4" : "#fffbeb",
                  border: `1px solid ${order.production_vendor_name ? "#bbf7d0" : "#fde68a"}`,
                  borderRadius: 6,
                  padding: "8px 10px",
                }}
                data-testid="vendor-assignment-box"
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b" }}>
                    Production Vendor
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: order.production_vendor_name ? "#dcfce7" : "#fef3c7",
                      color: order.production_vendor_name ? "#15803d" : "#b45309",
                    }}
                  >
                    {order.production_vendor_name ? "Assigned" : "Not Assigned"}
                  </span>
                </div>

                <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                  <Factory size={13} color="#4f46e5" />
                  <span>{order.production_vendor_name || "Unassigned"}</span>
                </div>
              </div>

              {/* 2. Assigned Production User Box */}
              <div
                style={{
                  background: (order.assigned_producer || order.assigned_production_user) ? "#f0fdf4" : "#fffbeb",
                  border: `1px solid ${(order.assigned_producer || order.assigned_production_user) ? "#bbf7d0" : "#fde68a"}`,
                  borderRadius: 6,
                  padding: "8px 10px",
                }}
                data-testid="user-assignment-box"
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b" }}>
                    Assigned User
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: (order.assigned_producer || order.assigned_production_user) ? "#dcfce7" : "#fef3c7",
                      color: (order.assigned_producer || order.assigned_production_user) ? "#15803d" : "#b45309",
                    }}
                  >
                    {(order.assigned_producer || order.assigned_production_user) ? "Assigned" : "Not Assigned"}
                  </span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                    <User size={13} color="#059669" />
                    <span>{order.assigned_producer || order.assigned_production_user || "Unassigned"}</span>
                  </div>
                  {isAdmin(user) && (
                    <button
                      type="button"
                      onClick={() => setShowAssignUserModal(true)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontSize: 10.5,
                        color: "#2563eb",
                        fontWeight: 600,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      title="Assign or change production user"
                      data-testid="assign-user-inline-btn"
                    >
                      {(order.assigned_producer || order.assigned_production_user) ? "Change" : "Assign"}
                    </button>
                  )}
                </div>
              </div>

              {/* 3. Procurement Warehouse Box */}
              <div
                style={{
                  background: order.warehouse_name ? "#f0fdf4" : "#fffbeb",
                  border: `1px solid ${order.warehouse_name ? "#bbf7d0" : "#fde68a"}`,
                  borderRadius: 6,
                  padding: "8px 10px",
                }}
                data-testid="warehouse-assignment-box"
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b" }}>
                    Warehouse
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: order.warehouse_name ? "#dcfce7" : "#fef3c7",
                      color: order.warehouse_name ? "#15803d" : "#b45309",
                    }}
                  >
                    {order.warehouse_name ? "Assigned" : "Not Assigned"}
                  </span>
                </div>

                <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                  <Warehouse size={13} color="#0284c7" />
                  <span>{order.warehouse_name || "Unassigned"}</span>
                </div>

                {order.warehouse_name && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed #e2e8f0", fontSize: 11 }}>
                    <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", fontWeight: 700, marginBottom: 3 }}>
                      Warehouse Production Staff
                    </div>
                    {(() => {
                      const whUsers = (order.warehouse_assigned_user_names && order.warehouse_assigned_user_names.length > 0)
                        ? order.warehouse_assigned_user_names
                        : (warehouses.find(w => w.warehouse_id === order.warehouse_id || w.name === order.warehouse_name)?.assigned_user_names || []);
                      if (!whUsers || whUsers.length === 0) {
                        return <span style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 10.5 }}>No warehouse users assigned</span>;
                      }
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {whUsers.map((name, i) => (
                            <span
                              key={i}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                color: "#0369a1",
                                background: "#e0f2fe",
                                border: "1px solid #bae6fd",
                                padding: "1px 6px",
                                borderRadius: 4,
                                fontWeight: 600,
                                fontSize: 10.5,
                              }}
                              data-testid={`warehouse-user-badge-${i}`}
                            >
                              <User size={10} color="#0284c7" /> {name}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* SECTION 4: PRODUCTION HISTORY (Collapsible) */}
          <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }} data-testid="production-history-panel">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setHistoryOpen(!historyOpen)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Clock size={15} color="#4f46e5" />
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>Production History</h3>
                <span style={{ fontSize: 11, color: "#64748b", background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>
                  {order.history?.length || 0} events
                </span>
              </div>
              <button
                type="button"
                className="icon-btn"
                style={{ padding: 2, color: "#64748b" }}
                aria-label={historyOpen ? "Collapse history" : "Expand history"}
              >
                {historyOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            </div>

            {historyOpen && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
                <OrderHistoryTrail history={order.history || []} />
              </div>
            )}
          </section>

        </div>

        {/* RIGHT COLUMN: SECTION 3: PRODUCTION UPDATES & ACTION CONSOLE */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          <section
            className="panel"
            style={{
              border: "1.5px solid #c7d2fe",
              background: "#ffffff",
              borderRadius: 8,
              padding: 16,
              boxShadow: "0 1px 3px rgba(79,70,229,0.06)",
            }}
            data-testid="production-updates-panel"
          >
            <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Production Updates &amp; Actions
              </div>
              <h3 style={{ margin: "2px 0 0", color: "var(--navy)", fontSize: 16, fontWeight: 700 }}>
                Status: {currentStatus}
              </h3>
            </div>

            {/* Hold Banner if applicable */}
            {currentStatus === "On Hold" && (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 6,
                  padding: "8px 10px",
                  marginBottom: 12,
                }}
                data-testid="production-hold-banner"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#92400e", fontWeight: 700, fontSize: 12, marginBottom: 2 }}>
                  <AlertCircle size={14} color="#b45309" /> On Hold Reason:
                </div>
                <div style={{ fontSize: 12, color: "#1e293b", whiteSpace: "pre-wrap" }}>
                  {order.hold_reason || order.production_remarks || "No reason specified"}
                </div>
                {currentEstDate && (
                  <div style={{ fontSize: 11, color: "#78350f", marginTop: 4 }}>
                    Next Ready Date: <b>{currentEstDate}</b>
                  </div>
                )}
              </div>
            )}

            {/* Current Production Metrics */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11.5, color: "#64748b" }}>Start Date:</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>
                  {order.production_start_date || "Not started"}
                </span>
              </div>

              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11.5, color: "#64748b" }}>Estimated Ready:</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: currentEstDate ? "#4f46e5" : "#64748b" }}>
                  {currentEstDate || "Not set"}
                </span>
              </div>

              {currentRemarks && (
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px" }}>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: "#64748b", display: "block", textTransform: "uppercase", marginBottom: 2 }}>Remarks</span>
                  <div style={{ fontSize: 11.5, color: "#334155", whiteSpace: "pre-wrap" }}>
                    {currentRemarks}
                  </div>
                </div>
              )}
            </div>

            {/* ACTION BUTTONS */}
            {canAct && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {/* When Not Started */}
                {currentStatus === "Not Started" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 600, color: "#334155", marginBottom: 4 }}>
                        Production Start Date
                      </label>
                      <input
                        type="date"
                        className="dispatch-control"
                        value={startDate}
                        min={getTodayDateString()}
                        onChange={(e) => setStartDate(e.target.value)}
                        data-testid="production-start-date-input"
                        style={{ height: 34, fontSize: 12.5 }}
                      />
                    </div>

                    <button
                      type="button"
                      className="primary-btn wide"
                      style={{ background: "#4f46e5", borderColor: "#4f46e5", height: 32, fontSize: 12, justifyContent: "center" }}
                      onClick={() => handleStartProduction(order.order_id)}
                      disabled={Boolean(busyAction)}
                      data-testid="start-production-button"
                    >
                      <Play size={13} /> Start Production
                    </button>
                  </div>
                )}

                {/* When In Progress */}
                {currentStatus === "In Progress" && (
                  <>
                    <button
                      type="button"
                      className="outline-btn wide"
                      onClick={() => setShowUpdateModal(true)}
                      disabled={Boolean(busyAction)}
                      data-testid="action-update-prod-btn"
                      style={{ height: 32, fontSize: 12, justifyContent: "center" }}
                    >
                      <Edit2 size={13} /> Update Production
                    </button>
                    <button
                      type="button"
                      className="outline-btn wide"
                      onClick={() => setShowHoldModal(true)}
                      disabled={Boolean(busyAction)}
                      data-testid="action-put-hold-btn"
                      style={{ color: "#b45309", borderColor: "#d97706", background: "#ffffff", height: 32, fontSize: 12, justifyContent: "center" }}
                    >
                      <Pause size={13} /> Put On Hold
                    </button>
                    <button
                      type="button"
                      className="primary-btn wide"
                      style={{ background: "#059669", borderColor: "#059669", height: 32, fontSize: 12, justifyContent: "center" }}
                      onClick={() => handleMarkCompleted(order.order_id)}
                      disabled={Boolean(busyAction)}
                      data-testid="action-complete-prod-btn"
                    >
                      <CheckCircle2 size={13} /> Mark Completed
                    </button>
                  </>
                )}

                {/* When On Hold */}
                {currentStatus === "On Hold" && (
                  <>
                    <button
                      type="button"
                      className="primary-btn wide"
                      style={{ background: "#4f46e5", borderColor: "#4f46e5", height: 32, fontSize: 12, justifyContent: "center" }}
                      onClick={() => setShowResumeModal(true)}
                      disabled={Boolean(busyAction)}
                      data-testid="action-resume-prod-btn"
                    >
                      <Play size={13} /> Resume Production
                    </button>
                    <button
                      type="button"
                      className="outline-btn wide"
                      onClick={() => setShowUpdateHoldModal(true)}
                      disabled={Boolean(busyAction)}
                      data-testid="action-update-hold-btn"
                      style={{ height: 32, fontSize: 12, justifyContent: "center" }}
                    >
                      <Edit2 size={13} /> Update Hold Details
                    </button>
                  </>
                )}

                {/* When Completed */}
                {currentStatus === "Completed" && (
                  <>
                    <div
                      style={{
                        background: "#ecfdf5",
                        border: "1px solid #a7f3d0",
                        borderRadius: 6,
                        padding: "8px 10px",
                        fontSize: 12,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#065f46", fontWeight: 700 }}>
                        <CheckCircle2 size={14} /> Production Completed
                      </div>
                      <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "#059669" }}>
                        Ready for Dispatch
                      </p>
                    </div>
                    <button
                      type="button"
                      className="primary-btn wide"
                      style={{ background: "#0284c7", borderColor: "#0284c7", height: 32, fontSize: 12, justifyContent: "center" }}
                      onClick={() => handleReadyForDispatch(order.order_id)}
                      disabled={Boolean(busyAction)}
                    >
                      <Send size={13} /> Send to Dispatch
                    </button>
                  </>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ============================================================== */}
      {/* ALL PRODUCTS OVERVIEW TABLE (Inside Bulk Batch)                */}
      {/* ============================================================== */}
      {isBatchMode && (
        <section className="panel" style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }}>
          <div style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>All Batch Products Overview</h3>
            <p className="muted" style={{ margin: "2px 0 0", fontSize: 11.5 }}>
              Manage individual production statuses for each product in this bulk request
            </p>
          </div>

          {batchData.orders.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 16px", background: "#f8fafc", borderRadius: 6, border: "1px dashed #cbd5e1" }}>
              <CheckCircle2 size={32} color="#059669" style={{ marginBottom: 8 }} />
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--navy)" }}>
                All products in this bulk request have been completed and handed over to Dispatch.
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                This production request has no remaining active production tasks.
              </p>
            </div>
          ) : (
            <div className="table-wrap" style={{ border: "1px solid #e2e8f0", borderRadius: 6 }}>
              <table data-testid="batch-products-overview-table">
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Order ID</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Product</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Quantity</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Status</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Start Date</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Ready Date</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Vendor</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Assigned User</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Warehouse</th>
                    <th style={{ padding: "8px 10px", fontSize: 11 }}>Remarks</th>
                    <th style={{ padding: "8px 10px", fontSize: 11, textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {batchData.orders.map((o) => {
                    const pStatus = o.production_status || "Not Started";
                    const bTheme = statusBadgeStyle(pStatus);
                    const isSelected = order?.order_id === o.order_id;

                    return (
                      <tr
                        key={o.order_id}
                        style={{
                          background: isSelected ? "#f0f9ff" : "transparent",
                          cursor: "pointer",
                        }}
                        onClick={() => handleSelectProduct(o)}
                        data-testid={`batch-table-row-${o.order_id}`}
                      >
                        <td style={{ padding: "8px 10px" }}>
                          <b className="mono" style={{ fontSize: 11.5 }}>#{o.order_id}</b>
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <strong style={{ fontSize: 12 }}>{o.product}</strong>
                          <div
                            style={{
                              fontSize: 11,
                              color: o.description?.trim() ? "#475569" : "#94a3b8",
                              fontStyle: o.description?.trim() ? "normal" : "italic",
                              marginTop: 2,
                              lineHeight: 1.3,
                            }}
                          >
                            {o.description?.trim() ? o.description.trim() : "No description provided"}
                          </div>
                          {o.design_specs && (
                            <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>
                              Specs: {o.design_specs}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", fontSize: 11.5 }}>
                          {o.quantity} {o.unit || "pcs"}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <span
                            style={{
                              fontSize: 9.5,
                              fontWeight: 700,
                              padding: "1px 5px",
                              borderRadius: 3,
                              color: bTheme.color,
                              background: bTheme.bg,
                              border: `1px solid ${bTheme.border}`,
                            }}
                          >
                            {pStatus}
                          </span>
                        </td>
                        <td style={{ padding: "8px 10px", fontSize: 11, color: "#64748b" }}>{o.production_start_date || "—"}</td>
                        <td style={{ padding: "8px 10px", fontSize: 11, color: "#64748b" }}>{o.estimated_ready_date || o.production_expected || "—"}</td>
                        <td style={{ padding: "8px 10px", fontSize: 11 }}>
                          {o.production_vendor_name ? (
                            <span>{o.production_vendor_name}</span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", fontSize: 11 }}>
                          {o.assigned_producer || o.assigned_production_user ? (
                            <span style={{ color: "#059669", fontWeight: 600 }}>{o.assigned_producer || o.assigned_production_user}</span>
                          ) : (
                            <span style={{ color: "#d97706" }}>Unassigned</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", fontSize: 11 }}>
                          {o.warehouse_name ? (
                            <span>{o.warehouse_name}</span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <small style={{ color: pStatus === "On Hold" ? "#b45309" : "#64748b", fontSize: 11 }}>
                            {o.hold_reason || o.production_remarks || "—"}
                          </small>
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                            {pStatus === "Completed" && (
                              <button
                                type="button"
                                className="primary-btn compact"
                                style={{ background: "#0284c7", borderColor: "#0284c7", padding: "2px 8px", fontSize: 11, height: 24, display: "inline-flex", alignItems: "center", gap: 3 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReadyForDispatch(o.order_id);
                                }}
                                disabled={Boolean(busyAction)}
                                title="Send this product to Dispatch"
                                data-testid={`batch-table-dispatch-btn-${o.order_id}`}
                              >
                                <Send size={10} /> {busyAction === `dispatch_${o.order_id}` ? "..." : "Send to Dispatch"}
                              </button>
                            )}
                            <button
                              type="button"
                              className={isSelected ? "primary-btn compact" : "outline-btn compact"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectProduct(o);
                              }}
                              style={{ padding: "2px 8px", fontSize: 11, height: 24 }}
                            >
                              {isSelected ? "Active" : "Manage"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ============================================================== */}
      {/* MODALS                                                         */}
      {/* ============================================================== */}

      {/* 1. Update Production Modal */}
      {showUpdateModal && (
        <UpdateProductionModal
          order={order}
          onClose={() => setShowUpdateModal(false)}
          onSave={handleSaveProductionUpdate}
        />
      )}

      {/* 2. Put On Hold Modal */}
      {showHoldModal && (
        <PutOnHoldModal
          order={order}
          onClose={() => setShowHoldModal(false)}
          onSave={handlePutOnHold}
        />
      )}

      {/* 3. Update Hold Details Modal */}
      {showUpdateHoldModal && (
        <UpdateHoldModal
          order={order}
          onClose={() => setShowUpdateHoldModal(false)}
          onSave={handleSaveHoldDetails}
        />
      )}

      {/* 4. Resume Production Modal */}
      {showResumeModal && (
        <ResumeProductionModal
          order={order}
          onClose={() => setShowResumeModal(false)}
          onSave={handleResumeProduction}
        />
      )}

      {/* Assign Production User Modal (Individual) */}
      {showAssignUserModal && order && (
        <AssignUserModal
          order={order}
          users={productionUsers}
          onClose={() => setShowAssignUserModal(false)}
          onSave={handleAssignProductionUser}
        />
      )}

      {/* Batch Assign Production User Modal */}
      {showBatchAssignUserModal && batchData && (
        <BatchAssignUserModal
          batch={batchData}
          users={productionUsers}
          onClose={() => setShowBatchAssignUserModal(false)}
          onSave={handleBatchAssignProductionUser}
        />
      )}

      {/* 5. Assign Vendor & Warehouse Modal (Individual) */}
      {showAssignModal && (
        <AssignVendorWarehouseModal
          order={order}
          vendors={vendors}
          warehouses={warehouses}
          onClose={() => setShowAssignModal(false)}
          onSave={handleAssignDetails}
        />
      )}

      {/* 6. Batch Assign Vendor & Warehouse Modal */}
      {showBatchAssignModal && batchData && (
        <BatchAssignModal
          batch={batchData}
          vendors={vendors}
          warehouses={warehouses}
          onClose={() => setShowBatchAssignModal(false)}
          onSave={handleBatchAssign}
        />
      )}

      {/* 7. Batch Start Production Modal */}
      {showBatchStartModal && batchData && (
        <BatchStartModal
          batch={batchData}
          onClose={() => setShowBatchStartModal(false)}
          onSave={handleBatchStart}
        />
      )}

      {/* 8. Batch Update Dates Modal */}
      {showBatchDateModal && batchData && (
        <BatchDateModal
          batch={batchData}
          onClose={() => setShowBatchDateModal(false)}
          onSave={handleBatchUpdateDates}
        />
      )}

      {/* 9. Move Back Stage Modal */}
      {movingBack && order && (
        <MoveBackStageModal
          order={order}
          isOpen={movingBack}
          onClose={() => setMovingBack(false)}
          onSuccess={() => {
            loadOrder();
            onChange && onChange();
          }}
          user={user}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------
// Individual Modals
// -------------------------------------------------------------
function UpdateProductionModal({ order, onClose, onSave }) {
  const [estDate, setEstDate] = useState(order.estimated_ready_date || order.production_expected || "");
  const [remark, setRemark] = useState(order.production_remarks || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ estDate, remark });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 460, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">PRODUCTION UPDATE</div>
            <h3>Update #{order.order_id} ({order.product})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Estimated Ready Date</label>
            <input
              type="date"
              className="dispatch-control"
              value={estDate}
              min={getTodayDateString()}
              onChange={(e) => setEstDate(e.target.value)}
              data-testid="modal-est-date-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Production Remarks / Progress Notes</label>
            <textarea
              className="dispatch-control"
              rows={3}
              placeholder="e.g. Printing completed, sent for die-cutting."
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              data-testid="modal-remarks-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" data-testid="save-production-update-btn">
              Save Updates
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PutOnHoldModal({ order, onClose, onSave }) {
  const [holdReason, setHoldReason] = useState("");
  const [nextEstDate, setNextEstDate] = useState(order.estimated_ready_date || order.production_expected || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ holdReason, nextEstDate });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 460, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#d97706" }}>STATUS CHANGE</div>
            <h3>Put Production On Hold (#{order.order_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">
              Hold Reason <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              className="dispatch-control"
              rows={3}
              placeholder="Reason why production is paused (e.g. Material shortage, waiting for client clarification)."
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              required
              data-testid="modal-hold-reason-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">
              Next Estimated Ready Date <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="date"
              className="dispatch-control"
              value={nextEstDate}
              min={getTodayDateString()}
              onChange={(e) => setNextEstDate(e.target.value)}
              required
              data-testid="modal-hold-next-date-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#d97706", borderColor: "#d97706" }}
              data-testid="confirm-put-hold-btn"
            >
              Put On Hold
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UpdateHoldModal({ order, onClose, onSave }) {
  const [holdReason, setHoldReason] = useState(order.hold_reason || order.production_remarks || "");
  const [nextEstDate, setNextEstDate] = useState(order.estimated_ready_date || order.production_expected || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ holdReason, nextEstDate });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 460, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#d97706" }}>HOLD MANAGEMENT</div>
            <h3>Update Hold Details (#{order.order_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">
              Hold Reason <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              className="dispatch-control"
              rows={3}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              required
              data-testid="modal-update-hold-reason-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">
              Next Estimated Ready Date <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="date"
              className="dispatch-control"
              value={nextEstDate}
              min={getTodayDateString()}
              onChange={(e) => setNextEstDate(e.target.value)}
              required
              data-testid="modal-update-hold-date-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" data-testid="save-hold-details-btn">
              Save Hold Details
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResumeProductionModal({ order, onClose, onSave }) {
  const [estDate, setEstDate] = useState(order.estimated_ready_date || order.production_expected || "");
  const [remark, setRemark] = useState("Production resumed");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ estDate, remark });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 460, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#4f46e5" }}>STATUS CHANGE</div>
            <h3>Resume Production (#{order.order_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Revised Estimated Ready Date</label>
            <input
              type="date"
              className="dispatch-control"
              value={estDate}
              min={getTodayDateString()}
              onChange={(e) => setEstDate(e.target.value)}
              data-testid="modal-resume-date-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Resume Remarks / Notes</label>
            <textarea
              className="dispatch-control"
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              data-testid="modal-resume-remarks-input"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
              data-testid="confirm-resume-prod-btn"
            >
              Resume Production
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignVendorWarehouseModal({ order, vendors, warehouses, onClose, onSave }) {
  const [vendorId, setVendorId] = useState(order.production_vendor_id || order.vendor_id || "");
  const [warehouseId, setWarehouseId] = useState(order.warehouse_id || "");

  useEffect(() => {
    let vId = order.production_vendor_id || order.vendor_id || "";
    if (!vId && (order.production_vendor_name || order.vendor_name || order.assigned_producer) && vendors.length > 0) {
      const vName = (order.production_vendor_name || order.vendor_name || order.assigned_producer).toLowerCase().trim();
      const match = vendors.find((v) => (v.name || v.vendor_name || "").toLowerCase().trim() === vName);
      if (match) vId = match.vendor_id;
    }

    let wId = order.warehouse_id || "";
    if (!wId && order.warehouse_name && warehouses.length > 0) {
      const wName = order.warehouse_name.toLowerCase().trim();
      const match = warehouses.find((w) => (w.name || w.warehouse_name || "").toLowerCase().trim() === wName);
      if (match) wId = match.warehouse_id;
    }

    if (wId && !vId) {
      setWarehouseId(wId);
      setVendorId("");
    } else if (vId) {
      setVendorId(vId);
      setWarehouseId("");
    } else {
      setVendorId("");
      setWarehouseId("");
    }
  }, [order, vendors, warehouses]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!vendorId && !warehouseId) {
      toast.error("Please select a Vendor or Warehouse.");
      return;
    }
    onSave({ vendorId, warehouseId });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 460, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">ASSIGNMENT</div>
            <h3>Assign Vendor or Warehouse (#{order.order_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Production Vendor</label>
            <select
              className="dispatch-control"
              value={vendorId}
              onChange={(e) => {
                const val = e.target.value;
                setVendorId(val);
                if (val) setWarehouseId("");
              }}
              data-testid="modal-vendor-select"
            >
              <option value="">-- Select Active Vendor (Optional) --</option>
              {vendors.length === 0 && (
                <option value="" disabled>No active vendors available in Production Resources</option>
              )}
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.name || v.vendor_name} {v.contact_person ? `(${v.contact_person})` : (v.location ? `· ${v.location}` : "")}
                </option>
              ))}
            </select>
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Procurement Warehouse</label>
            <select
              className="dispatch-control"
              value={warehouseId}
              onChange={(e) => {
                const val = e.target.value;
                setWarehouseId(val);
                if (val) setVendorId("");
              }}
              data-testid="modal-warehouse-select"
            >
              <option value="">-- Select Active Warehouse (Optional) --</option>
              {warehouses.length === 0 && (
                <option value="" disabled>No active warehouses available in Production Resources</option>
              )}
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name || w.warehouse_name} {w.location || w.address ? `· ${(w.location || w.address).slice(0, 30)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" data-testid="save-assignment-btn">
              Save Assignment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Modals
// -------------------------------------------------------------
function BatchAssignModal({ batch, vendors, warehouses, onClose, onSave }) {
  const [vendorId, setVendorId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [selectedIds, setSelectedIds] = useState(batch.orders.map((o) => o.order_id));

  const toggleAll = () => {
    if (selectedIds.length === batch.orders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(batch.orders.map((o) => o.order_id));
    }
  };

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!vendorId && !warehouseId) {
      toast.error("Please select a Vendor or Warehouse.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one product.");
      return;
    }
    onSave({ vendorId, warehouseId, orderIds: selectedIds });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 500, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">BULK ASSIGNMENT</div>
            <h3>Assign Vendor or Warehouse (Batch #{batch.batch_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Production Vendor</label>
            <select
              className="dispatch-control"
              value={vendorId}
              onChange={(e) => {
                const val = e.target.value;
                setVendorId(val);
                if (val) setWarehouseId("");
              }}
              data-testid="batch-modal-vendor-select"
            >
              <option value="">-- Select Active Vendor (Optional) --</option>
              {vendors.length === 0 && (
                <option value="" disabled>No active vendors available in Production Resources</option>
              )}
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.name || v.vendor_name} {v.contact_person ? `(${v.contact_person})` : (v.location ? `· ${v.location}` : "")}
                </option>
              ))}
            </select>
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Procurement Warehouse</label>
            <select
              className="dispatch-control"
              value={warehouseId}
              onChange={(e) => {
                const val = e.target.value;
                setWarehouseId(val);
                if (val) setVendorId("");
              }}
              data-testid="batch-modal-warehouse-select"
            >
              <option value="">-- Select Active Warehouse (Optional) --</option>
              {warehouses.length === 0 && (
                <option value="" disabled>No active warehouses available in Production Resources</option>
              )}
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name || w.warehouse_name} {w.location || w.address ? `· ${(w.location || w.address).slice(0, 30)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label className="dispatch-label" style={{ margin: 0 }}>
                Apply to Products ({selectedIds.length}/{batch.orders.length})
              </label>
              <button type="button" className="outline-btn compact" onClick={toggleAll} style={{ padding: "2px 6px", fontSize: 11 }}>
                {selectedIds.length === batch.orders.length ? "Deselect All" : "Select All"}
              </button>
            </div>

            <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 6px" }}>
              {batch.orders.map((o) => (
                <label
                  key={o.order_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 6px",
                    fontSize: 12,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(o.order_id)}
                    onChange={() => toggleOne(o.order_id)}
                  />
                  <span>
                    <strong>{o.product}</strong> (#{o.order_id}) — {o.quantity} {o.unit || "pcs"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" data-testid="batch-save-assignment-btn">
              Apply ({selectedIds.length})
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BatchStartModal({ batch, onClose, onSave }) {
  const [startDateVal, setStartDateVal] = useState(new Date().toISOString().slice(0, 10));
  const notStartedOrders = batch.orders.filter((o) => (o.production_status || "Not Started") === "Not Started");
  const [selectedIds, setSelectedIds] = useState(
    notStartedOrders.map((o) => o.order_id)
  );

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      toast.error("Select at least one product to start.");
      return;
    }
    onSave({ startDateVal, orderIds: selectedIds });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#4f46e5" }}>BULK START</div>
            <h3>Start Production for Batch (#{batch.batch_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Production Start Date</label>
            <input
              type="date"
              className="dispatch-control"
              value={startDateVal}
              min={getTodayDateString()}
              onChange={(e) => setStartDateVal(e.target.value)}
              data-testid="batch-modal-start-date"
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="dispatch-label" style={{ display: "block", marginBottom: 6 }}>
              Select Products to Start ({selectedIds.length}/{notStartedOrders.length || batch.orders.length})
            </label>

            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 6px" }}>
              {batch.orders.map((o) => {
                const st = o.production_status || "Not Started";
                const isNotStarted = st === "Not Started";
                return (
                  <label
                    key={o.order_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "4px 6px",
                      fontSize: 12,
                      cursor: isNotStarted ? "pointer" : "not-allowed",
                      opacity: isNotStarted ? 1 : 0.6,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(o.order_id)}
                        disabled={!isNotStarted}
                        onChange={() => isNotStarted && toggleOne(o.order_id)}
                      />
                      <span>
                        <strong>{o.product}</strong> (#{o.order_id})
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "1px 5px",
                        borderRadius: 3,
                        color: st === "Completed" ? "#059669" : st === "In Progress" ? "#7c3aed" : st === "On Hold" ? "#d97706" : "#0284c7",
                        background: st === "Completed" ? "#ecfdf5" : st === "In Progress" ? "#f5f3ff" : st === "On Hold" ? "#fffbeb" : "#f0f9ff",
                        border: `1px solid ${st === "Completed" ? "#a7f3d0" : st === "In Progress" ? "#ddd6fe" : st === "On Hold" ? "#fde68a" : "#bae6fd"}`,
                      }}
                    >
                      {st}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
              data-testid="batch-confirm-start-btn"
              disabled={selectedIds.length === 0}
            >
              Start Selected ({selectedIds.length})
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BatchDateModal({ batch, onClose, onSave }) {
  const [estDate, setEstDate] = useState("");
  const [remark, setRemark] = useState("");
  const [selectedIds, setSelectedIds] = useState(batch.orders.map((o) => o.order_id));

  const toggleOne = (oid) => {
    if (selectedIds.includes(oid)) {
      setSelectedIds(selectedIds.filter((x) => x !== oid));
    } else {
      setSelectedIds([...selectedIds, oid]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!estDate) {
      toast.error("Please enter an estimated ready date.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one product.");
      return;
    }
    onSave({ estDate, remark, orderIds: selectedIds });
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 480, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label">BULK DATE UPDATE</div>
            <h3>Set Estimated Ready Date (Batch #{batch.batch_id})</h3>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">
              Estimated Ready Date <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="date"
              className="dispatch-control"
              value={estDate}
              min={getTodayDateString()}
              onChange={(e) => setEstDate(e.target.value)}
              required
              data-testid="batch-modal-est-date"
            />
          </div>

          <div className="dispatch-field" style={{ marginTop: 12 }}>
            <label className="dispatch-label">Progress Remarks</label>
            <textarea
              className="dispatch-control"
              rows={2}
              placeholder="e.g. Bulk order in cutting phase."
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              data-testid="batch-modal-remarks"
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="dispatch-label" style={{ display: "block", marginBottom: 6 }}>
              Apply to Products ({selectedIds.length}/{batch.orders.length})
            </label>

            <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 6px" }}>
              {batch.orders.map((o) => (
                <label
                  key={o.order_id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 6px",
                    fontSize: 12,
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(o.order_id)}
                    onChange={() => toggleOne(o.order_id)}
                  />
                  <span>
                    <strong>{o.product}</strong> (#{o.order_id})
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" data-testid="batch-confirm-dates-btn">
              Apply Dates ({selectedIds.length})
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// -------------------------------------------------------------
// Individual Assign Production User Modal
// -------------------------------------------------------------
function AssignUserModal({ order, users, onClose, onSave }) {
  const currentAssigned = order.assigned_producer || order.assigned_production_user || "";
  const initialUser = users.find((u) => u.name === currentAssigned || u.user_id === order.producer_id || u.user_id === order.production_user_id);
  const [selectedUserId, setSelectedUserId] = useState(initialUser ? initialUser.user_id : currentAssigned ? "__custom__" : "");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let assignedUser = "";
      let assignedUserId = "";
      if (selectedUserId && selectedUserId !== "__unassign__") {
        const found = users.find((u) => u.user_id === selectedUserId);
        if (found) {
          assignedUser = found.name;
          assignedUserId = found.user_id;
        } else if (selectedUserId === "__custom__") {
          assignedUser = currentAssigned;
          assignedUserId = order.producer_id || order.production_user_id || "";
        }
      }
      await onSave({ assignedUser, assignedUserId, remarks: remarks.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 460, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#059669" }}>PRODUCTION ASSIGNMENT</div>
            <h3>Assign Production User (#{order.order_id})</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, color: "#166534" }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>#{order.order_id} • {order.product}</div>
            <div>Client: {order.client_name} • {order.quantity} {order.unit || "units"}</div>
            {currentAssigned && (
              <div style={{ marginTop: 4, fontSize: 11.5, color: "#15803d" }}>
                Current production user: <b>{currentAssigned}</b>
              </div>
            )}
          </div>

          <div className="dispatch-field">
            <label className="dispatch-label">Internal Production User *</label>
            <select
              className="dispatch-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="select-production-user-detail"
              required
            >
              <option value="">-- Choose Production User --</option>
              <option value="__unassign__">❌ Unassign / Remove User</option>
              {users.map((u) => (
                <option key={u.user_id || u.name} value={u.user_id}>
                  {u.name} ({u.role || u.department || "Staff"})
                </option>
              ))}
            </select>
          </div>

          <div className="dispatch-field" style={{ marginTop: 14 }}>
            <label className="dispatch-label">Remarks / Instructions</label>
            <textarea
              className="dispatch-control"
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Assigned to monitor print quality"
              data-testid="assign-user-remarks-detail"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#059669", borderColor: "#059669", display: "inline-flex", alignItems: "center", gap: 6 }}
              disabled={submitting || !selectedUserId}
              data-testid="confirm-assign-user-detail-btn"
            >
              <UserCheck size={13} />
              <span>{submitting ? "Saving..." : selectedUserId === "__unassign__" ? "Confirm Unassign" : "Save Assignment"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Batch Assign Production User Modal
// -------------------------------------------------------------
function BatchAssignUserModal({ batch, users, onClose, onSave }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedIds, setSelectedIds] = useState(batch.orders.map((o) => o.order_id));
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleSelectOrder = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      toast.error("Please select at least one product.");
      return;
    }
    setSubmitting(true);
    try {
      let assignedUser = "";
      let assignedUserId = "";
      if (selectedUserId && selectedUserId !== "__unassign__") {
        const found = users.find((u) => u.user_id === selectedUserId);
        if (found) {
          assignedUser = found.name;
          assignedUserId = found.user_id;
        }
      }
      await onSave({
        assignedUser,
        assignedUserId,
        orderIds: selectedIds,
        remarks: remarks.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 520, width: "95%" }}>
        <div className="modal-header">
          <div>
            <div className="small-label" style={{ color: "#059669" }}>BATCH ASSIGNMENT</div>
            <h3>Assign Production User (Batch #{batch.batch_id})</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={submitting}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px" }}>
          <div className="dispatch-field">
            <label className="dispatch-label">Internal Production User *</label>
            <select
              className="dispatch-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="batch-select-production-user"
              required
            >
              <option value="">-- Choose Production User --</option>
              <option value="__unassign__">❌ Unassign / Remove User</option>
              {users.map((u) => (
                <option key={u.user_id || u.name} value={u.user_id}>
                  {u.name} ({u.role || u.department || "Staff"})
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="dispatch-label" style={{ marginBottom: 6, display: "block" }}>
              Apply to Products ({selectedIds.length}/{batch.orders.length})
            </label>
            <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 10px", background: "#f8fafc" }}>
              {batch.orders.map((o) => (
                <label key={o.order_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer", fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(o.order_id)}
                    onChange={() => toggleSelectOrder(o.order_id)}
                  />
                  <span><b>#{o.order_id}</b> — {o.product} ({o.quantity} {o.unit || "pcs"})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="dispatch-field" style={{ marginTop: 14 }}>
            <label className="dispatch-label">Remarks (Optional)</label>
            <textarea
              className="dispatch-control"
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Batch assigned to production manager"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              style={{ background: "#059669", borderColor: "#059669", display: "inline-flex", alignItems: "center", gap: 6 }}
              disabled={submitting || !selectedUserId || selectedIds.length === 0}
              data-testid="confirm-batch-assign-user-btn"
            >
              <UserCheck size={13} />
              <span>{submitting ? "Saving..." : "Apply to Selected Products"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
